import isEqual from 'lodash/isEqual'
import {
    useCallback, useMemo, useState, useEffect,
} from 'react'
import useAlternativeFlowRequests
    from 'app/Apps/AccountManagement/AccountLanes/AccountLaneForm/hooks/useAlternativeFlowRequests'
import {
    AccountLane,
    AccountLaneForm as AccountLaneFormType,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'

import {
    Country,
} from 'app/Apps/ContactManagement/Countries/Countries.types'
import {
    isCollection, isDeliveryRoad, showFTL, showLTL,
} from 'app/Apps/Pricing/utils/utils'
import {
    LaneStatus,
} from 'app/enums/label.enums'
import useEnumValues from 'app/hooks/useEnumValues'
import useFieldsWithClassNames from 'app/hooks/useFieldsWithClassName'
import useHasPermission from 'app/hooks/useHasPermission'
import useValidation from 'app/hooks/useValidation'
import FormWithControls from 'app/shared-components/FormWithControls'
import useAccountManagementRoutes from 'app/SkyNetRoutes/hooks/useAccountManagementRoutes'

import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes/skyNetRoutes.utils'
import {
    DTOCardFormField,
} from 'app/types/form.types'
import {
    RequestConfig,
} from 'app/types/request.types'

import useGetLaneRequestedAwardedProductTypes from '../hooks/useGetLaneRequestedAwardedProductTypes'

import {
    AccountLaneFormFields,
} from '../AccountLaneUpdate/statusIsPriced.fields'
import getFields from './AccountLaneForm.fields'
import useStyles from './AccountLaneForm.styles'

export type AccountLaneFormFieldsArgs = {
    getAllowedValues: (val: Record<string, string>) => Record<string, string>,
    showFTL: boolean,
    showLTL: boolean,
    isDeliveryRoad: boolean,
    isCollection: boolean,
    handoverPointCountry?: Country | { id: number },
    collectionDropoffPointCountry?: Country | { id: number },
    showProductTypes?: boolean,
    showAwardedProductTypes?: boolean,
    exists: boolean,
    contractBasisAdminPermission: boolean,
    getAddressCreationLink?: () => JSX.Element[] | string[],
    laneLeadPermission?: boolean,
    requiredBillingCompany?: boolean
}

const defaultProps = {
    onCancel: undefined,
    data: {} as AccountLane,
    disabled: false,
    exists: false,
    customButtons: undefined,
    getCustomFields: undefined,
    getCustomButtons: undefined,
    getAddressCreationLink: undefined,
    customClasses: {},
}

export default function AccountLaneForm({
    exists,
    data,
    requestParam,
    onSuccess,
    onCancel,
    disabled,
    customButtons: predefinedCustomButtons,
    getCustomFields,
    getCustomButtons,
    getAddressCreationLink,
    customClasses,
}: Readonly<{
    data?: AccountLane,
    requestParam: RequestConfig,
    onSuccess: (...args: any[]) => void,
    onCancel?: (...args: any[]) => void,
    disabled?: boolean,
    exists?: boolean
    customButtons?: JSX.Element[],
    getCustomFields?: (
        args: Partial<AccountLaneFormFieldsArgs>
    ) => DTOCardFormField<AccountLaneFormType>[] | DTOCardFormField<keyof AccountLaneFormFields>[],
    getCustomButtons?: ({
        isValid, synced,
    }: {
        isValid: boolean,
        synced?: boolean,
    }) => JSX.Element[],
    getAddressCreationLink?: () => JSX.Element[],
    customClasses?: Record<string, string>,
}>) {
    const showProductTypes = data?.id && (data?.laneStatus !== LaneStatus.TO_BE_QUOTED)
    const {
        CQuotes: CQuotesRoutes,
        AQuotes: AQuotesRoutes,
    } = useAccountManagementRoutes()
    const isAccountAdmin = useHasPermission(['account_admin'])

    const {
        data: productTypes,
    } = useGetLaneRequestedAwardedProductTypes(showProductTypes ? data?.id : undefined)

    const getAllowedValues = useEnumValues()

    const [
        value,
        setValue,
    ] = useState<AccountLane>(data)
    const contractBasisAdminPermission = useHasPermission(['contract_basis_admin'])

    const {
        isAlternativeFlow,
        logisticDetails,
    } = useAlternativeFlowRequests(value, disabled)

    const showHandoverCollectionSection = useMemo(() => {
        let requiredValues = [
            exists ? (value?.account || value?.contractBasisAccount) : value?.account,
            value?.temperatureRange,
            value?.leaseType,
        ]

        if (isAlternativeFlow) {
            requiredValues = [
                ...requiredValues,
                value?.originAirport,
                value?.destinationAirport,
            ]
        }

        return requiredValues.every(Boolean)
    }, [
        exists,
        isAlternativeFlow,
        value?.account,
        value?.contractBasisAccount,
        value?.destinationAirport,
        value?.leaseType,
        value?.originAirport,
        value?.temperatureRange,
    ])

    const {
        classes,
    } = useStyles({
        showProductTypes,
        showHandoverCollectionSection,
    })

    const handleCancel = useCallback(() => {
        if (onCancel) {
            onCancel()
            return
        }

        setValue(data)
    }, [
        onCancel,
        data,
    ])

    const getFieldsFunc = useMemo(() => {
        if (getCustomFields) {
            return getCustomFields
        }
        return getFields
    }, [getCustomFields])

    const fields = useFieldsWithClassNames(
        getFieldsFunc({
            getAllowedValues,
            isDeliveryRoad: isDeliveryRoad(value?.deliveryServiceType),
            showFTL: showFTL(value?.transportModeRoad),
            showLTL: showLTL(value?.transportModeRoad),
            isCollection: isCollection(value?.collectionServiceType),
            opportunityFilters: {
                accountId: [value?.account?.id],
                temperatureRange: [value?.temperatureRange],
            },
            contractBasisAdminPermission,
            collectionDropoffPointCountry: value?.collectionDropoffPointCountry,
            handoverPointCountry: value?.handoverPointCountry,
            getAddressCreationLink,
            showProductTypes,
            showAwardedProductTypes: [
                LaneStatus.AWARDED,
                LaneStatus.IMPLEMENTED,
            ].includes(data?.laneStatus),
            exists,
            quoteInLink: (value?.cQuoteId || value.aQuoteId) ? generateSkyNetLink({
                domainPath: value?.cQuoteId
                    ? CQuotesRoutes.Overview.path
                    : AQuotesRoutes.Overview.path,
                params: {
                    key: value?.cQuoteId || value.aQuoteId,
                },
            }) : null,
            quoteInNumber: value?.cQuoteNumber || value?.aQuoteNumber,
            tempRangeDisabled: exists && data?.laneStatus !== LaneStatus.TO_BE_QUOTED,
            accountDisabled: exists && Boolean(data.account?.id),
            disabled: disabled || data?.laneStatus === LaneStatus.CANCELLED,
            laneLeadPermission: isAccountAdmin || !exists,
            requiredBillingCompany: [
                LaneStatus.AWARDED,
                LaneStatus.IMPLEMENTED,
                LaneStatus.CANCELLED,
                LaneStatus.INACTIVE,
            ].includes(data?.laneStatus),
            showHandoverCollectionSection,
            logisticDetails,
            isAlternativeFlow,
        }), classes,
    )

    const synced = isEqual(data, value)

    // clear deliveryServiceType and collectionServiceType when flow changes
    useEffect(() => {
        if (synced) return
        setValue((prev) => {
            if (!prev?.id || [
                LaneStatus.UNDER_REVIEW_PRICING,
                LaneStatus.TO_BE_QUOTED,
            ].includes(prev.laneStatus)) {
                return {
                    ...prev,
                    deliveryServiceType: undefined,
                    transportModeRoad: undefined,
                    collectionServiceType: undefined,
                    isFtlPositioningTemperatureControlled: undefined,
                    isLtlPositioningTemperatureControlled: undefined,
                    handoverPointCountry: undefined,
                    handoverPointAddress: undefined,
                    handoverPointZipCode: undefined,
                    collectionDropoffPointCountry: undefined,
                    collectionDropoffPointAddress: undefined,
                    collectionDropoffPointZipCode: undefined,
                }
            }

            return prev
        })
    }, [ // eslint-disable-line react-hooks/exhaustive-deps
        isAlternativeFlow,
        logisticDetails,
    ])

    const formValues = useMemo(() => {
        return {
            ...value,
            isLtlPositioningTemperatureControlled:
                (isDeliveryRoad(value.deliveryServiceType)
                    && value?.isLtlPositioningTemperatureControlled === undefined)
                    ? true : value?.isLtlPositioningTemperatureControlled,
            isFtlPositioningTemperatureControlled:
                (isDeliveryRoad(value.deliveryServiceType)
                    && value?.isFtlPositioningTemperatureControlled === undefined)
                    ? true : value?.isFtlPositioningTemperatureControlled,
            requestedProductTypes: productTypes?.requestedProductTypes,
            awardedProductTypes: productTypes?.awardedProductTypes,
        }
    }, [
        value,
        productTypes,
    ])

    const modifyDataBeforeSend = useCallback((accountLane: AccountLane) => {
        let payload = accountLane

        if (!isDeliveryRoad(accountLane.deliveryServiceType)) {
            payload = {
                ...payload,
                transportModeRoad: undefined,
                isFtlPositioningTemperatureControlled: undefined,
                isLtlPositioningTemperatureControlled: undefined,
                handoverPointZipCode: undefined,
                handoverPointAddress: undefined,
                handoverPointCountry: undefined,
            }
        }

        if (!showLTL(accountLane.transportModeRoad)) {
            payload = {
                ...payload,
                isLtlPositioningTemperatureControlled: undefined,
            }
        }

        if (!showFTL(accountLane.transportModeRoad)) {
            payload = {
                ...payload,
                isFtlPositioningTemperatureControlled: undefined,
            }
        }

        if (!isCollection(accountLane.collectionServiceType)) {
            payload = {
                ...payload,
                collectionDropoffPointZipCode: undefined,
                collectionDropoffPointAddress: undefined,
                collectionDropoffPointCountry: undefined,
            }
        }

        return payload
    }, [])

    const {
        isValid,
    } = useValidation({
        fields,
        values: formValues,
    })

    const customButtons = useMemo(() => {
        if (getCustomButtons) {
            return getCustomButtons({
                isValid, synced,
            })
        }
        return predefinedCustomButtons
    }, [
        getCustomButtons,
        predefinedCustomButtons,
        isValid,
        synced,
    ])

    return (
        <FormWithControls
            disabled={disabled}
            value={formValues}
            name="AccountLane"
            requestParams={requestParam}
            setExternalValue={setValue}
            fields={fields}
            onSuccess={onSuccess}
            onCancel={handleCancel}
            modifyDataBeforeSend={modifyDataBeforeSend}
            classNames={{
                gridWrapper: classes.gridWrapper,
                ...customClasses,
            }}
            exists={exists}
            customButtons={customButtons}
        />
    )
}

AccountLaneForm.defaultProps = defaultProps
